import { Injectable } from '@angular/core';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { ShopifyService } from './shopify.service';
import { FirebaseService } from './firebase.service';
import { AwsUploadService } from './aws-upload.service';
import { DesignQueueService } from './design-queue.service';
import { PromoService } from './promo.service';
import { MetaPixelService } from './meta-pixel.service';
import { CartService } from './cart.service';

export interface CheckoutState {
  isProcessingOrder: boolean;
  buyInProgress: boolean;
  cartId: string | null;
}

export interface DesignAttribute {
  designIndex: number;
  pdfUrl: string;
  magnetUrls: string;
  quantity: number;
}

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  private state: CheckoutState = {
    isProcessingOrder: false,
    buyInProgress: false,
    cartId: null
  };

  private buyTimeout: any = null;

  constructor(
    private shopifyService: ShopifyService,
    private firebaseService: FirebaseService,
    private awsUploadService: AwsUploadService,
    private designQueueService: DesignQueueService,
    private promoService: PromoService,
    private metaPixelService: MetaPixelService,
    private analytics: Analytics,
    private cartService: CartService
  ) {}

  /**
   * Get current checkout state
   */
  getState(): CheckoutState {
    return { ...this.state };
  }

  /**
   * Check if checkout is in progress
   */
  isProcessing(): boolean {
    return this.state.isProcessingOrder || this.state.buyInProgress;
  }

  /**
   * Optional cart info for Meta Pixel / CAPI AddToCart event (value, currency).
   */
  static metaCartInfoFrom(value: number, currency: string): { value: number; currency: string } {
    return { value, currency };
  }

  /**
   * Start checkout process - creates cart and initiates the checkout flow
   */
  startCheckout(
    totalQuantity: number,
    onStateChange?: (state: CheckoutState) => void,
    metaCartInfo?: { value: number; currency: string }
  ): void {
    // Prevent multiple simultaneous checkout attempts
    if ((this.designQueueService.isEmpty() && this.cartService.getShopifyProducts().length === 0) || this.isProcessing()) {
      return;
    }

    this.state.buyInProgress = true;
    if (onStateChange) {
      onStateChange({ ...this.state });
    }

    // Clear any existing timeout
    if (this.buyTimeout) {
      clearTimeout(this.buyTimeout);
    }

    this.buyTimeout = setTimeout(() => {
      this.state.isProcessingOrder = true;
      if (onStateChange) {
        onStateChange({ ...this.state });
      }

      // Get country code from session storage
      const countryCode = sessionStorage.getItem('country') || 'MK';

      // Step 1: Create cart
      this.shopifyService.createCart(countryCode).subscribe({
        next: (res: any) => {
          if (res?.data?.cartCreate?.cart?.id) {
            this.state.cartId = res.data.cartCreate.cart.id;
            this.addAllDesignsToCart(totalQuantity, onStateChange, metaCartInfo);
          } else {
            console.error('Error creating cart:', res?.data?.cartCreate?.userErrors);
            this.resetState();
            if (onStateChange) {
              onStateChange({ ...this.state });
            }
          }
        },
        error: (error) => {
          console.error('Error creating cart:', error);
          this.resetState();
          if (onStateChange) {
            onStateChange({ ...this.state });
          }
        }
      });
    }, 300); // 300ms debounce
  }

  /**
   * Add all designs to cart and process them
   */
  private async addAllDesignsToCart(
    totalQuantity: number,
    onStateChange?: (state: CheckoutState) => void,
    metaCartInfo?: { value: number; currency: string }
  ): Promise<void> {
    const customDesignsQuantity = this.designQueueService.getTotalQuantity();
    const shopifyProducts = this.cartService.getShopifyProducts();
    
    // Build line items array
    const lines: Array<{ variantId: string; quantity: number }> = [];

    // Add custom magnet line items (if any)
    if (customDesignsQuantity > 0) {
      const isValentinesActive = this.promoService.isValentinesActive();
      const customLines = isValentinesActive
        ? this.promoService.getValentinesLineItems(customDesignsQuantity)
        : (() => {
            const mainShop = sessionStorage.getItem('main');
            const productVariantId = mainShop === 'true'
              ? 'gid://shopify/ProductVariant/42366700585041'
              : 'gid://shopify/ProductVariant/56263898431813';
            return [{ variantId: productVariantId, quantity: customDesignsQuantity }];
          })();
      lines.push(...customLines);
    }

    // Add Shopify product line items (if any)
    shopifyProducts.forEach(item => {
      lines.push({
        variantId: item.product.variantId,
        quantity: item.quantity
      });
    });

    this.shopifyService.addLinesToCart(this.state.cartId!, lines).subscribe({
      next: async (res: any) => {
        try {
          if (res?.data?.cartLinesAdd?.cart?.checkoutUrl) {
            const checkoutUrl = res.data.cartLinesAdd.cart.checkoutUrl;

            // Meta Pixel + CAPI: AddToCart (same event_id for prioritization)
            if (metaCartInfo && metaCartInfo.value > 0 && metaCartInfo.currency) {
              const contentIds = lines.map((l) => {
                const gid = l.variantId;
                const match = gid && /ProductVariant\/(\d+)/.exec(gid);
                return match ? match[1] : gid;
              }).filter(Boolean);
              this.metaPixelService.trackAddToCart(
                metaCartInfo.value,
                metaCartInfo.currency,
                contentIds.length ? contentIds : undefined
              );
            }

            // Process all custom designs (only if there are any)
            const designAttributes = customDesignsQuantity > 0 
              ? await this.processAllDesigns() 
              : [];

            const referralCode = sessionStorage.getItem('rc');
            const referralMap: Record<string, string> = {
              'vDYggbHDnl': 'petshop1',
              'kJFs82kdla': 'cool_pethsop',
              'Xz29AhpQa9': 'instagram_influencer'
            };
            const mappedReferral = referralMap[referralCode || ''] || '';

            // Update cart with all design attributes
            this.shopifyService.updateCartAttributesMultiple(
              this.state.cartId!,
              designAttributes,
              mappedReferral
            ).subscribe({
              next: () => {
                logEvent(this.analytics, 'ecommerceCheckoutStarted');
                this.resetState();
                if (onStateChange) {
                  onStateChange({ ...this.state });
                }
                window.location.assign(checkoutUrl);
              },
              error: (attrError) => {
                console.error('Error adding URLs to cart:', attrError);
                // Still redirect to checkout even if attaching URLs fails
                this.resetState();
                if (onStateChange) {
                  onStateChange({ ...this.state });
                }
                // window.location.assign(checkoutUrl);
              }
            });
          } else {
            console.error('Error adding product to cart:', res?.data?.cartLinesAdd?.userErrors);
            this.resetState();
            if (onStateChange) {
              onStateChange({ ...this.state });
            }
          }
        } catch (error) {
          console.error('Error in cart process:', error);
          this.resetState();
          if (onStateChange) {
            onStateChange({ ...this.state });
          }
        }
      },
      error: (error) => {
        console.error('Error adding product to cart:', error);
        this.resetState();
        if (onStateChange) {
          onStateChange({ ...this.state });
        }
      }
    });
  }

  /**
   * Process all designs - upload images to Firebase, PDFs to AWS, and create story documents
   */
  private async processAllDesigns(): Promise<DesignAttribute[]> {
    const designAttributes: DesignAttribute[] = [];
    const designs = this.designQueueService.getDesigns();

    for (let i = 0; i < designs.length; i++) {
      const design = designs[i];
      const filename = `magnet-design-${Date.now()}-${i}`;

      try {
        // Upload to Firebase
        const magnetImageUrl = await this.firebaseService.uploadToFirebase(
          design.base64Image,
          filename
        );
        const magnetDesignId = await this.firebaseService.magnetDesignDocument(
          magnetImageUrl,
          design.location
        );

        // Upload PDF to AWS
        const pdfUrl = await this.awsUploadService.uploadPdf(design.pdfBlob, filename);

        // Create URLs for each quantity of this design
        const magnetUrls: string[] = [];
        for (let j = 0; j < design.quantity; j++) {
          const storyId = await this.firebaseService.storyDocument();
          magnetUrls.push(`https://gulli.travel/?m=${magnetDesignId}&s=${storyId}`);
        }

        designAttributes.push({
          designIndex: i + 1,
          pdfUrl: pdfUrl,
          magnetUrls: magnetUrls.join('||'),
          quantity: design.quantity
        });
      } catch (error) {
        console.error(`Error processing design ${i + 1}:`, error);
        // Continue with other designs even if one fails
      }
    }

    return designAttributes;
  }

  /**
   * Reset checkout state
   */
  private resetState(): void {
    this.state.isProcessingOrder = false;
    this.state.buyInProgress = false;
    this.state.cartId = null;
    if (this.buyTimeout) {
      clearTimeout(this.buyTimeout);
      this.buyTimeout = null;
    }
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.resetState();
  }
}

